/**
 * The trend detail chart — the expanded form of a trend row (REQ-STATUS-075;
 * design-system → Component patterns → Trend detail chart).
 *
 * A sparkline answers *"is anything moving?"*. This answers the question an
 * operator reaches for next — *"how far past the line did it go, and when?"* —
 * which the 40 px form cannot, because it carries no y scale, no time scale and
 * deliberately no way to read a value off it.
 *
 * ⚠ **This is a different mark, not a large sparkline.** The sparkline's *"no
 * axes, no gridlines, no point markers"* is a property of a 40 px shape: at
 * that height an axis is unreadable for everyone. At 200 px it is readable, and
 * reading a number off it is the entire reason to expand. **None of those
 * prohibitions transfer.** What does transfer is the line — 1.5 px ``--ring``,
 * ``vector-effect="non-scaling-stroke"``, round joins, no fill, no markers, and
 * gaps that **break the path** (REQ-STATUS-073).
 *
 * # Tick labels are HTML, and that is load-bearing
 *
 * The plot stretches its internal coordinate system to the container with
 * ``preserveAspectRatio="none"``, which is what lets one viewBox serve a 350 px
 * docked pane and a 1400 px maximized one. That transform scales **glyphs**
 * non-uniformly: an SVG ``<text>`` inside this viewport renders smeared at any
 * non-square aspect. ``vector-effect`` rescues the stroke width; nothing
 * rescues a glyph. So every label here is real HTML, positioned over the plot —
 * which also keeps it selectable, zoomable, and styled by the same classes the
 * rest of the pane uses.
 *
 * # Three marks wear this name
 *
 * :func:`buildDetailSeries` routes on the catalogued unit, and two of the eight
 * units are **not magnitudes**: a ``boolean`` renders as a band (a boolean over
 * time is a *state*), and an ``epoch_seconds`` renders as an **age** —
 * ``bucket_time − value``, a sawtooth resetting on every run, relabelled so
 * nobody reads the axis as the timestamp.
 *
 * # Threshold reference lines (REQ-STATUS-076)
 *
 * The element that earns the feature: it converts *"why is this red?"* from an
 * inference into something visible, and shows **how far past** and **when
 * crossed**. The values are the collector's own, so the line and the stoplight
 * cannot disagree.
 *
 * Colour is the **disposable** channel here (REQ-UI-046) — the dash pattern and
 * the edge label each carry the meaning alone, so the lines survive a greyscale
 * screenshot. Two cases draw nothing: a metric with no threshold (absence means
 * *"cannot escalate"*, never *"within bounds"*), and a **derived** series,
 * whose axis plots something the raw bound does not describe.
 *
 * # What this phase does not do
 *
 * No crosshair, no keyboard cursor, no width-derived tick counts — those land
 * with REQ-STATUS-077 and -078. The tick counts are already parameters so that
 * last one is a caller change, not a rewrite.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import type React from "react";
import { DescriptionTooltip } from "@/components/ui/tooltip";
import { BOOLEAN_BAND } from "@/system-status/band-vocabulary";
import { StateBand, type TrendPoint } from "@/system-status/status-detail-primitives";
import type { MetricUnit } from "@/system-status/metric-units";
import {
  bucketAtFraction,
  buildDetailSeries,
  extendRangeForThresholds,
  formatReading,
  thresholdMarks,
  tickCountsForWidth,
  valueRange,
  xAxisTicks,
  yAxisTicks,
  type AxisTick,
  type MetricThreshold,
} from "@/system-status/trend-detail-model";
import type { HistoryWindow } from "@/system-status/use-component-history";

/** Internal coordinate width. The rendered width is always the container's. */
const PLOT_WIDTH = 600;

/** Nominal plot height (design-system → Trend detail chart). */
const PLOT_HEIGHT = 200;

/** The expanded band's height — taller than the 18 px inline form, far short of a plot. */
const BAND_HEIGHT = 48;

/**
 * Width of the y-tick gutter.
 *
 * Narrowed from 4.5rem on 2026-08-03: that width was sized for `1,204,315` — a
 * label the axis almost never shows — and every chart paid for it by indenting
 * the plot 72 px from the pane's left edge, which is a large fraction of a
 * docked ~350 px pane. 2.75rem fits the labels the axis actually renders
 * (`47.6%`, `15m`, `2h`, `1.5 KB`) with headroom.
 *
 * ⚠ Referenced by BOTH the gutter and the x-axis row's offset. They must agree
 * or tick 0 stops sitting under the plot's left edge, so this is one constant
 * used twice rather than two numbers kept in step by hand.
 */
const Y_GUTTER = "2.75rem";

/**
 * Tick counts assumed until the container has been measured.
 *
 * A docked Selection Detail pane is the common case, so the fallback matches
 * it rather than a maximized one: over-ticking a narrow chart for one frame
 * collides labels, while under-ticking a wide one for a frame is merely sparse.
 * Fail toward the readable state.
 */
const FALLBACK_WIDTH = 400;

export interface TrendDetailChartProps {
  /** ``[epoch_seconds, value]`` per bucket; ``null`` is a **gap**. */
  points: readonly TrendPoint[];
  /**
   * The VISIBLE label.
   *
   * Passed through as given — including any ``(max)`` suffix the caller has
   * already applied for an aggregate-sourced series (REQ-STATUS-064 amended).
   * The chart does **not** re-derive that: which windows are aggregate-sourced
   * is the server's routing table's fact, and a second copy of it here is a
   * copy that drifts.
   */
  label: string;
  /** Accessible name — metric and window (REQ-STATUS-070). Defaults to the label. */
  accessibleName?: string;
  /** Catalog description, disclosed on the shared tooltip (REQ-STATUS-056). */
  description?: string;
  /** A control rendered at the end of the label row — the collapse toggle. */
  disclosure?: React.ReactNode;
  /** The catalogued unit. ``undefined`` plots a plain magnitude (REQ-STATUS-054). */
  unit?: MetricUnit;
  /**
   * The collector's ``{warn, crit}`` pair, where the metric has one
   * (REQ-STATUS-076).
   *
   * **Absent on most metrics, and that absence is meaningful** — it says the
   * metric cannot drive the stoplight at all, which is different from one
   * sitting safely below its bound. Only the ~7 rollup-driving metrics carry a
   * threshold.
   */
  threshold?: MetricThreshold;
  /** The selected window — sets the x-tick resolution. */
  window: HistoryWindow;
  testId?: string;
  /**
   * Override the width-derived x-tick count (REQ-STATUS-078).
   *
   * Left unset in production — the count comes from the measured container, and
   * a caller that pins it defeats the responsive rule. Present so a test can
   * assert a count without driving a resize.
   */
  xTickCount?: number;
  /** Override the width-derived y-tick count. Same caveat as `xTickCount`. */
  yTickCount?: number;
  height?: number;
}

export function TrendDetailChart({
  points,
  label,
  accessibleName,
  description,
  unit,
  threshold,
  disclosure,
  window,
  testId = "trend-detail",
  xTickCount,
  yTickCount,
  height = PLOT_HEIGHT,
}: TrendDetailChartProps): React.ReactElement {
  const series = buildDetailSeries({ points, label, unit });
  // REQ-STATUS-078 — the counts follow the CONTAINER, not the viewBox (which is
  // a fixed 600 units) and not the window (which never changes on a resize).
  const { ref: sizeRef, width } = useElementWidth();
  const counts = tickCountsForWidth(width ?? FALLBACK_WIDTH);
  const xTicks = xAxisTicks(points, window, xTickCount ?? counts.x);
  // The cursor, and HOW it was placed. The source matters: the live region
  // speaks only for keyboard moves, because a pointer user is reading the
  // tooltip and announcing every pixel of travel would flood a screen reader
  // for someone not navigating by it (REQ-STATUS-077).
  const [cursor, setCursor] = useState<{ index: number; source: "pointer" | "keyboard" } | null>(
    null,
  );

  // ⚠ The label ELEMENT carries the metric name AND the controls beside it —
  // the catalog tooltip trigger and the expansion toggle — so its `textContent`
  // is no longer the metric name. The name therefore has its own handle: an
  // assertion on "what is this row called" must not pick up "?" and "+".
  // Found by the §5.11 live pass, where a merged `toHaveText(/^CPU$/)` began
  // reading "CPU?+".
  const heading = (
    <p
      className="flex items-center gap-1 text-meta text-text-secondary"
      data-testid={`${testId}-label`}
    >
      <span data-testid={`${testId}-label-text`}>{series.label}</span>
      {description !== undefined && description !== "" && (
        <DescriptionTooltip testId={testId} label={series.label} description={description} />
      )}
      {disclosure}
    </p>
  );

  // A boolean is a state over time, so it gets the band and the time axis — but
  // no y axis, because there is no magnitude to scale.
  if (series.kind === "band") {
    if (series.points.length === 0) return <Empty testId={testId}>{heading}</Empty>;
    return (
      <div className="mt-1" data-testid={testId}>
        <div className="rounded-sm bg-bg-surface px-2 py-1.5" data-testid={`${testId}-surface`}>
          {heading}
          <StateBand
            points={series.points}
            label={series.label}
            accessibleName={accessibleName ?? series.label}
            vocabulary={BOOLEAN_BAND}
            height={BAND_HEIGHT}
          />
          <XAxis ticks={xTicks} testId={testId} gutter="0" />
        </div>
      </div>
    );
  }

  const dataRange = valueRange(series.points);
  const known = series.points.filter((point) => point[1] !== null);
  // Under two known buckets there is no line to draw and no range to state.
  // Saying so is the honest render; a single point joined to nothing is not.
  if (dataRange === null || known.length < 2) return <Empty testId={testId}>{heading}</Empty>;

  // ⚠ A threshold is a bound on the RAW value. A derived series plots something
  // else entirely — an `epoch_seconds` axis carries an AGE — so its threshold
  // would land at a wrong height under a confident label. Suppressed rather
  // than rescaled, because there is no honest rescaling of an epoch bound into
  // a duration (REQ-STATUS-076).
  const marksFor = series.derived ? undefined : threshold;
  const range = extendRangeForThresholds(dataRange, marksFor);
  const marks = thresholdMarks(range, marksFor, series.unit);

  const yTicks = yAxisTicks(range, yTickCount ?? counts.y, series.unit);
  const span = range.max - range.min;
  const step = series.points.length > 1 ? PLOT_WIDTH / (series.points.length - 1) : PLOT_WIDTH;

  const count = series.points.length;
  const move = (index: number, source: "pointer" | "keyboard"): void => {
    setCursor({ index: Math.min(count - 1, Math.max(0, index)), source });
  };
  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>): void => {
    if (event.key === "Escape") {
      setCursor(null);
      return;
    }
    const from = cursor?.index ?? 0;
    // Arrows step, Home/End jump. Nothing WRAPS: taking an operator from the
    // newest bucket to the oldest with no signal that the time axis jumped a
    // whole window is a worse answer than stopping at the end.
    const to =
      event.key === "ArrowRight"
        ? from + 1
        : event.key === "ArrowLeft"
          ? from - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? count - 1
              : null;
    if (to === null) return;
    event.preventDefault();
    move(to, "keyboard");
  };
  const onPointerMove = (event: PointerEvent<SVGSVGElement>): void => {
    // The viewBox is 600 wide but renders at the container's width, so the
    // pointer's fraction must come from the RENDERED box — using PLOT_WIDTH
    // here would misplace every reading at any width but exactly 600 px.
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width === 0) return;
    move(bucketAtFraction(count, (event.clientX - box.left) / box.width), "pointer");
  };
  const reading =
    cursor === null
      ? null
      : formatReading(series.points[cursor.index], series.unit, series.derived);

  // Runs of consecutive known values, each its own polyline. Joining across a
  // gap would draw a segment asserting a continuity that was never measured
  // (REQ-STATUS-073).
  const runs: string[][] = [];
  let run: string[] = [];
  series.points.forEach(([, value], index) => {
    if (value === null) {
      if (run.length > 0) runs.push(run);
      run = [];
      return;
    }
    const y = height - ((value - range.min) / span) * height;
    run.push(`${(index * step).toFixed(1)},${y.toFixed(1)}`);
  });
  if (run.length > 0) runs.push(run);

  return (
    <div className="mt-1" data-testid={testId}>
      {/* The shade marks the EXTENT of the expansion, so it wraps the whole
          row — label, gutter, plot and x axis alike. Stopping it at the plot
          (as the first visual pass did) left the label and the tick row
          outside the shape they belong to. Matched to the Selection Detail
          section header's surface so it reads as the same family of chrome
          rather than a new one. */}
      <div
        ref={sizeRef}
        className="rounded-sm bg-bg-surface px-2 py-1.5"
        data-testid={`${testId}-surface`}
      >
        {heading}
        <div className="flex gap-2">
          {/* The y gutter. Right-aligned to match the pane's numeric column, and
            each tick pulled up by half its own height so it centres on the
            gridline it names rather than hanging below it. */}
          <div
            className="relative shrink-0"
            style={{ height, width: Y_GUTTER }}
            data-testid={`${testId}-y-ticks`}
          >
            {yTicks.map((tick) => (
              <span
                key={tick.at}
                className="absolute right-0 whitespace-nowrap text-meta tabular-nums text-text-secondary"
                style={{ top: `${tick.at * 100}%`, transform: verticalAnchor(tick.at) }}
              >
                {tick.text}
              </span>
            ))}
          </div>
          <div className="relative min-w-0 flex-1" data-testid={`${testId}-plot-area`}>
            <svg
              viewBox={`0 0 ${PLOT_WIDTH} ${height}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={accessibleName ?? series.label}
              data-testid={`${testId}-plot`}
              data-runs={runs.length}
              className="block w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              style={{ height }}
              // One tab stop per chart, with the arrows moving INSIDE it — the
              // roving-focus convention. With several charts expanded at once
              // (REQ-STATUS-074 amended), a tab stop per bucket would put
              // hundreds of stops between an operator and the next section.
              tabIndex={0}
              onKeyDown={onKeyDown}
              onPointerMove={onPointerMove}
              onPointerLeave={() => setCursor(null)}
              onBlur={() => setCursor(null)}
            >
              {/* Horizontal only. The x ticks carry the vertical, and a full grid
              at this density competes with the line it exists to support. */}
              {yTicks.map((tick) => (
                <line
                  key={tick.at}
                  data-grid=""
                  x1={0}
                  x2={PLOT_WIDTH}
                  y1={tick.at * height}
                  y2={tick.at * height}
                  stroke="hsl(var(--border))"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {/* The axis frame. Added by the 2026-08-03 visual pass: gridlines
                alone gave the expanded plot no edge, so it read as one more
                sparkline in the stack rather than as a chart. Drawn in
                `--decor-muted`, a step above the gridlines' `--border`, so the
                frame is legible against its own grid while staying well short
                of competing with the series. */}
              <line
                data-axis="y"
                x1={0}
                x2={0}
                y1={0}
                y2={height}
                stroke="hsl(var(--decor-muted))"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <line
                data-axis="x"
                x1={0}
                x2={PLOT_WIDTH}
                y1={height}
                y2={height}
                stroke="hsl(var(--decor-muted))"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              {/* Threshold reference lines. Colour is REDUNDANT here: the dash
              pattern and the edge label each carry the meaning on their own, so
              the line survives a greyscale screenshot and a reader who cannot
              separate amber from red (REQ-UI-046). */}
              {marks.map((mark) => (
                <line
                  key={mark.kind}
                  data-threshold={mark.kind}
                  x1={0}
                  x2={PLOT_WIDTH}
                  y1={mark.at * height}
                  y2={mark.at * height}
                  stroke={mark.stroke}
                  strokeWidth={1.5}
                  strokeDasharray={mark.dash}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {/* The crosshair. Drawn before the series so the line it points
                  at stays on top — the cursor locates a reading, it does not
                  compete with it. */}
              {cursor !== null && (
                <line
                  data-cursor=""
                  data-testid={`${testId}-cursor`}
                  x1={cursor.index * step}
                  x2={cursor.index * step}
                  y1={0}
                  y2={height}
                  stroke="hsl(var(--decor-muted))"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              )}
              {runs.map((segment, index) => (
                <polyline
                  key={index}
                  points={segment.join(" ")}
                  fill="none"
                  // `--ring` is the brand accent LINE colour. NOT `--accent`, which
                  // is shadcn's interaction-state SURFACE slot and paints the line
                  // in roughly the panel's own background — invisible in BOTH
                  // themes. That shipped once and was caught only by a visual pass.
                  stroke="hsl(var(--ring))"
                  strokeWidth={1.5}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
            {/* The reading. HTML for the same reason the ticks are, and pinned to
            the top of the plot rather than following the pointer — a tooltip that
            chases the cursor across a 200 px plot is harder to read than one that
            stays put, and it cannot cover the series it describes. */}
            {reading !== null && (
              <span
                data-testid={`${testId}-reading`}
                className="pointer-events-none absolute left-1/2 top-1 -translate-x-1/2 whitespace-nowrap rounded border border-border bg-bg-elevated px-1.5 py-0.5 text-meta tabular-nums text-text-primary"
              >
                {reading}
              </span>
            )}
            {/* ⚠ A LOCAL live region, deliberately NOT the app-level announcer.
            That announcer serves discrete app events (panel added, layout reset)
            and re-announces via an empty-then-fill cycle plus a 5 s auto-clear on
            every call — right for infrequent events, wrong for a value that
            changes on every keypress, and it would put the cursor in contention
            with panel announcements on one shared region. A reading is a property
            of the focused widget, which is what a widget-owned region is for
            (REQ-UI-059). */}
            <span
              data-testid={`${testId}-live`}
              className="sr-only"
              role="status"
              aria-live="polite"
            >
              {cursor?.source === "keyboard" && reading !== null ? reading : ""}
            </span>
            {/* Edge labels, HTML for the same reason the ticks are (see the module
            docstring). Right-aligned to the plot's own right edge. */}
            {marks.map((mark) => (
              <span
                key={mark.kind}
                data-testid={`${testId}-threshold-${mark.kind}`}
                className={`absolute right-0 whitespace-nowrap bg-bg-surface px-1 text-meta tabular-nums ${
                  mark.kind === "crit"
                    ? "text-meridian-status-unhealthy"
                    : "text-meridian-status-degraded"
                }`}
                style={{ top: `${mark.at * 100}%`, transform: verticalAnchor(mark.at) }}
              >
                {mark.text}
              </span>
            ))}
          </div>
        </div>
        <XAxis ticks={xTicks} testId={testId} gutter={Y_GUTTER} />
      </div>
    </div>
  );
}

/**
 * The rendered width of an element, remeasured as it changes.
 *
 * The tick rule is a function of the CONTAINER's width, and the plot's own
 * viewBox is a fixed 600 units — so the count cannot be derived from anything
 * static. A panel drag resizes the pane without resizing the window, which is
 * why this observes the element rather than listening for `resize`.
 */
function useElementWidth(): { ref: React.RefObject<HTMLDivElement>; width: number | null } {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const measure = (): void => {
      const measured = element.getBoundingClientRect().width;
      // Ignore a zero measurement rather than storing it: a detached or
      // not-yet-laid-out element would otherwise pin the chart to its narrowest
      // tick counts and never recover.
      if (measured > 0) setWidth(measured);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/**
 * Keep a label anchored INSIDE the plot at either extreme.
 *
 * ⚠ The visual pass of 2026-08-03 caught this as a **functional** bug, not a
 * cosmetic one. With the axis extended to include `crit` (REQ-STATUS-076), crit
 * lands at ``at = 0``; a blanket ``translateY(-50%)`` then puts half the label
 * ABOVE the plot, over the label row — where the collapse control sits, also
 * right-aligned. The chart could be expanded and then **not collapsed**.
 *
 * The x ticks already clamped their HORIZONTAL anchor at the ends for exactly
 * this reason; this is that rule applied to the axis it was missing from.
 */
function verticalAnchor(at: number): string {
  if (at <= 0.02) return "translateY(0)";
  if (at >= 0.98) return "translateY(-100%)";
  return "translateY(-50%)";
}

/**
 * The x-tick row, offset past the y gutter so tick 0 sits under the plot's
 * left edge.
 *
 * The first and last ticks are anchored to the ends rather than centred on
 * them: a centred edge tick overflows the plot, and the container would clip
 * it — which is *truncation*, and a truncated timestamp is a wrong number
 * (REQ-STATUS-078).
 */
function XAxis({
  ticks,
  testId,
  gutter,
}: {
  ticks: readonly AxisTick[];
  testId: string;
  gutter: string;
}): React.ReactElement {
  return (
    <div
      className="relative h-4"
      style={{ marginLeft: gutter === "0" ? undefined : `calc(${gutter} + 0.5rem)` }}
      data-testid={`${testId}-x-ticks`}
    >
      {ticks.map((tick) => (
        <span
          key={tick.at}
          className="absolute whitespace-nowrap text-meta tabular-nums text-text-secondary"
          style={{
            left: `${tick.at * 100}%`,
            transform:
              tick.at === 0 ? undefined : tick.at === 1 ? "translateX(-100%)" : "translateX(-50%)",
          }}
        >
          {tick.text}
        </span>
      ))}
    </div>
  );
}

/**
 * The honest empty render — the label stays, so the row is still identifiable
 * in a stack, and the copy says which absence this is.
 */
function Empty({
  testId,
  children,
}: {
  testId: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mt-1" data-testid={testId}>
      {children}
      <p className="text-meta italic text-text-muted" data-testid={`${testId}-empty`}>
        not enough history yet
      </p>
    </div>
  );
}
